/**
 * MetricTile — Penpot "Metric / X" tile (Work Orders / Appointments mockup).
 *
 * Visual: white card, 1px solid grey border, 8px rounded corners, content
 * centered, big bold number on top, small uppercase label below, with a 5px
 * colored accent bar at the bottom (border-bottom, full width of the card).
 *
 * Reusable for any "single KPI tile" — pass `value` (number) or `amountCents`
 * ($ formatted) plus a `color` for the accent bar. Color accepts a hex/rgb
 * string OR an "R,G,B" triplet from public.status.color_v2 (auto-wrapped).
 */

import * as React from "react";
import { formatCurrencyCents, formatRgb } from "./types";

export interface MetricTileProps {
  label: string;
  /** Numeric value when the tile is a count. */
  value?: number | null;
  /**
   * Primary monetary value (cents) when the tile is a $ total. If both
   * `value` and `amountCents` are set, `amountCents` wins as the big number.
   */
  amountCents?: number | null;
  /**
   * Optional secondary $ amount rendered smaller, between the big value and
   * the label. Used by tiles that show BOTH a count and a $ total
   * (e.g. Invoices ALL OPEN: 54 invoices / $65,942.38).
   */
  subAmountCents?: number | null;
  /** Color for the bottom accent bar; "R,G,B" or any CSS color. */
  color?: string | null;
  /** When true, render skeleton placeholders for the body. */
  loading?: boolean;
  /** Optional click handler (e.g. drill down). */
  onClick?: () => void;
  className?: string;
}

export function MetricTile({
  label,
  value,
  amountCents,
  subAmountCents,
  color,
  loading,
  onClick,
  className = "",
}: MetricTileProps) {
  const display =
    amountCents !== undefined && amountCents !== null
      ? formatCurrencyCents(amountCents)
      : value !== undefined && value !== null
        ? value.toLocaleString("en-US")
        : "—";
  const subDisplay =
    subAmountCents !== undefined && subAmountCents !== null
      ? formatCurrencyCents(subAmountCents)
      : null;
  const isInteractive = typeof onClick === "function";
  const accentColor = formatRgb(color, "rgb(229,231,235)"); // gray-200 fallback

  return (
    <div
      role={isInteractive ? "button" : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      onClick={onClick}
      onKeyDown={
        isInteractive
          ? (e) => (e.key === "Enter" || e.key === " ") && onClick?.()
          : undefined
      }
      className={`relative bg-white rounded-lg border border-gray-200 flex flex-col items-center text-center pt-3 px-2 pb-4 min-h-[100px] overflow-hidden ${
        isInteractive ? "cursor-pointer hover:border-gray-300 transition-colors" : ""
      } ${className}`}
    >
      {loading ? (
        <>
          <div className="h-7 w-10 bg-gray-100 rounded animate-pulse" />
          {subDisplay !== null && (
            <div className="h-3 w-14 bg-gray-100 rounded animate-pulse mt-1" />
          )}
          <div className="h-3 w-16 bg-gray-100 rounded animate-pulse mt-1.5" />
        </>
      ) : (
        <>
          <span className="text-[28px] leading-tight font-bold text-gray-800 tabular-nums">
            {display}
          </span>
          {subDisplay !== null && (
            <span className="mt-0.5 text-[11px] font-semibold text-gray-500 tabular-nums">
              {subDisplay}
            </span>
          )}
          <span
            className="mt-1 text-[10px] font-semibold tracking-wider text-gray-400 uppercase"
            title={label}
          >
            {label}
          </span>
        </>
      )}
      {/* Bottom accent bar — full width, anchored to the card edge. */}
      <span
        aria-hidden
        className="absolute bottom-0 left-0 right-0 h-[5px]"
        style={{ backgroundColor: accentColor }}
      />
    </div>
  );
}
